import { useTranslation } from 'react-i18next';
import { MainCustomButton } from '../../components/Buttons';
import { CustomInput } from '../../components/inputs';
import { AuthPageLayout } from './AuthPageLayout';
import * as Yup from 'yup';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuthService } from '../../services/AuthService';
import { errorToast } from '../../utils/toasts';
import { errorMessageAdapter } from '../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../../plugins/i18n/i18n';
import { useApi } from '../../services/hooks/apiHook';
import { useAppSelector } from '../../hooks/hooksRedux';
import { RootState } from '../../store';
import { randomInt } from '../../utils/random';
import { useMemo } from 'react';

interface authInfos {
  email: string;
  password: string;
  password_confirmation: string;
}

const ResetPasswordPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get('token');

  const { logo } = useAppSelector((state: RootState) => state.styleGuide.data);
  const randomImage = useMemo(() => randomInt(1, 5), []);

  const api: AxiosInstance = useApi();

  const validationSchema = Yup.object().shape({
    /* eslint-disable no-useless-escape */
    email: Yup.string()
      .required(t('validations.valRequired') || '')
      .email(t('validations.valInvalidEmail') || ''),
    password: Yup.string()
      .required(t('validations.valRequired') || '')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/, t('validations.valPwdRule') || ''),
    password_confirmation: Yup.string()
      .required(t('validations.valRequired') || '')
      .oneOf([Yup.ref('password')], t('validations.valPwdMustMatch') || '')
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<authInfos>({
    resolver: yupResolver(validationSchema)
  });

  const handleSubmitForm: SubmitHandler<authInfos> = async (data: authInfos) => {
    const payload = { ...data, token: token };
    try {
      await AuthService.resetPassword(api, payload, {
        headers: { 'Content-Language': i18n.language.toLowerCase() }
      });
      navigate('/login');
    } catch (e: any) {
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  return (
    <AuthPageLayout imageIndex={randomImage} imageText={t('labels.lblWeveHelped')} logo={logo} title={t('buttons.btnResetPwd')}>
      <form
        id="MainForm"
        className="flex w-full flex-col items-start justify-center gap-5"
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <div className="flex w-full flex-col gap-5">
          <CustomInput
            id="email"
            label={t('labels.lblEmail')}
            placeholder={t('fields.fldEmailPlaceholder') || ' '}
            error={errors.email?.message}
            register={register}
            autoComplete="off"
          />
          <CustomInput
            id="password"
            label={t('labels.lblPassword')}
            placeholder={t('fields.fldConfirmPwdPlaceholder') || ''}
            type="password"
            tooltip={t('validations.valPwdAdvice') as string}
            error={errors.password?.message}
            register={register}
            autoComplete="off"
          />
          <CustomInput
            id="password_confirmation"
            label={t('labels.lblConfirmPassword')}
            placeholder={t('fields.fldConfirmPwdPlaceholder') || ''}
            type="password"
            error={errors.password_confirmation?.message}
            register={register}
            autoComplete="off"
          />
        </div>
        <MainCustomButton text={t('buttons.btnResetPwd')} form="MainForm" className="!max-w-none" />
      </form>
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
